/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface CancellationSecondaryReasonProps {
  id: string;
  name: string;
  cancellationReasonId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class CancellationSecondaryReasonRecord extends Record<CancellationSecondaryReasonProps>({
  id: null as any,
  name: null as any,
  cancellationReasonId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    cancellationReasonId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): CancellationSecondaryReasonRecord {
    if (json == null) {
      throw new Error('CancellationSecondaryReasonRecord.fromJS: parameter cannot be null');
    }
    return new CancellationSecondaryReasonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'CancellationSecondaryReasonJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'CancellationSecondaryReasonJson.name'),
      cancellationReasonId: getFromOrThrow(
        json,
        (j) =>
          propValueOrDefault(j, 'cancellationReasonId', '00000000-0000-0000-0000-000000000000'),
        'CancellationSecondaryReasonJson.cancellationReasonId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    cancellationReasonId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [CancellationSecondaryReasonRecord, Error[]] {
    if (json == null) {
      throw new Error('CancellationSecondaryReasonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new CancellationSecondaryReasonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'CancellationSecondaryReasonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'CancellationSecondaryReasonJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cancellationReasonId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'cancellationReasonId', '00000000-0000-0000-0000-000000000000'),
            'CancellationSecondaryReasonJson.cancellationReasonId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: CancellationSecondaryReasonProps) {
    super(props);
    if (props == null) {
      throw new Error('CancellationSecondaryReasonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class CancellationSecondaryReasonViewModelBase
  implements CancellationSecondaryReasonProps
{
  protected readonly _props: CancellationSecondaryReasonProps;
  private _json?: CancellationSecondaryReasonProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get cancellationReasonId(): string {
    return this._props.cancellationReasonId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: CancellationSecondaryReasonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        cancellationReasonId: this._props.cancellationReasonId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllCancellationSecondaryReasonEntities = (state: AppState) =>
  state.entityStores.cancellationSecondaryReason.entities;

export const selectCancellationSecondaryReasonById = (id: string) => (state: AppState) =>
  state.entityStores.cancellationSecondaryReason.entities.get(id);

export const selectCancellationSecondaryReasonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.cancellationSecondaryReason.meta.get(id);

export const selectCancellationSecondaryReasonAndMetadataById = (id: string) =>
  createSelector(
    selectCancellationSecondaryReasonById(id),
    selectCancellationSecondaryReasonMetadataById(id),
    (cancellationSecondaryReason, meta) => ({ cancellationSecondaryReason, meta }),
  );

export function selectCancellationSecondaryReasonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllCancellationSecondaryReasonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, CancellationSecondaryReasonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectCancellationSecondaryReasonEntitiesByName(name: string) {
  return createSelector(selectAllCancellationSecondaryReasonEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectCancellationSecondaryReasonEntitiesByCancellationReasonId(
  cancellationReasonId: string,
) {
  return createSelector(selectAllCancellationSecondaryReasonEntities, (entities) =>
    entities.filter((e) => e.cancellationReasonId === cancellationReasonId).valueSeq(),
  );
}
//#endregion Selectors
